import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  EmployeeOfferDetails,
  MonetaryDetailsV2,
  PaymentDetails,
} from "../../../../types/Transaction";
import { correctApprovedPayments } from "../../../../api/TransactionApisV2";
import {
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSize,
  HStack,
  VStack,
} from "../../../../components/utils";
import {
  Input,
  Label,
  TextArea,
} from "../../../../components/shared/InputField";
import { Select } from "../../../../components/shared/Select";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../components/shared/RadioGroup";
import { Card, CardContent } from "../../../../components/shared/Card";

type PaymentType = "Exercise Price" | "Tax Price";
type EditablePayment = {
  id?: string;
  updatedAt?: string;
  paymentType: PaymentType;
  referenceId: string;
  amount: number;
  currency: string;
  paymentDate: string;
  metaData?: PaymentDetails["metaData"];
};
type Props = {
  monetaryDetails: MonetaryDetailsV2;
  employee?: EmployeeOfferDetails;
  transactionId?: string;
  onClose: () => void;
  onSuccess: () => void;
};
const MODES = [
  "UPI",
  "NetBanking",
  "RTGS",
  "NEFT",
  "Card-Based Payments",
  "Others",
];
const dateValue = (date?: string | Date) => {
  const parsed = date ? new Date(date) : new Date();
  return Number.isNaN(parsed.getTime())
    ? new Date().toISOString().slice(0, 10)
    : parsed.toISOString().slice(0, 10);
};
const displayName: Record<PaymentType, string> = {
  "Exercise Price": "Exercise payment",
  "Tax Price": "Tax payment",
};
const normalizePaymentType = (
  paymentType?: string
): PaymentType | undefined => {
  switch (paymentType?.toLowerCase()) {
    case "exercise price":
      return "Exercise Price";
    case "tax price":
      return "Tax Price";
    default:
      return undefined;
  }
};

export default function CorrectApprovedPaymentDetails({
  monetaryDetails,
  employee,
  transactionId,
  onClose,
  onSuccess,
}: Props) {
  const approved = useMemo<EditablePayment[]>(
    () =>
      (employee?.paymentDetails || []).flatMap((payment) => {
        const paymentType = normalizePaymentType(payment.paymentType);
        if (payment.paymentStatus !== "SUCCESS" || !paymentType) {
          return [];
        }
        const updatedAt = (
          payment as PaymentDetails & { updatedAt?: string | Date }
        ).updatedAt;
        return [
          {
            id: payment.id,
            updatedAt: updatedAt
              ? new Date(updatedAt).toISOString()
              : undefined,
            paymentType,
            referenceId: payment.referenceId,
            amount: Number(payment.amount),
            currency: payment.currency || "INR",
            paymentDate: dateValue(payment.paymentDate),
            metaData: payment.metaData,
          },
        ];
      }),
    [employee?.paymentDetails]
  );
  const [payments, setPayments] = useState(approved);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const types = (["Exercise Price", "Tax Price"] as PaymentType[]).filter(
    (type) => approved.some((payment) => payment.paymentType === type)
  );
  const targetFor = (type: PaymentType) =>
    Number(
      type === "Exercise Price"
        ? employee?.transactionCompensationDetails?.totalStrikePrice || 0
        : employee?.transactionCompensationDetails?.totalTaxAmount || 0
    );
  const totalFor = (type: PaymentType) =>
    payments
      .filter((payment) => payment.paymentType === type)
      .reduce((total, payment) => total + payment.amount, 0);
  const deletedCount = approved.filter(
    (payment) =>
      payment.id && !payments.some((current) => current.id === payment.id)
  ).length;
  const update = (index: number, field: keyof EditablePayment, value: string) =>
    setPayments((current) =>
      current.map((payment, currentIndex) =>
        currentIndex === index
          ? { ...payment, [field]: field === "amount" ? Number(value) : value }
          : payment
      )
    );
  const updateMode = (index: number, modeOfPayment: string) =>
    setPayments((current) =>
      current.map((payment, currentIndex) =>
        currentIndex === index
          ? { ...payment, metaData: { ...payment.metaData, modeOfPayment } }
          : payment
      )
    );
  const add = (paymentType: PaymentType) =>
    setPayments((current) => [
      ...current,
      {
        paymentType,
        referenceId: "",
        amount: Math.max(0, targetFor(paymentType) - totalFor(paymentType)),
        currency: "INR",
        paymentDate: dateValue(),
        metaData: { financingType: "SELF", modeOfPayment: "" },
      },
    ]);
  const financingFor = (paymentType: PaymentType) =>
    payments.find((payment) => payment.paymentType === paymentType)?.metaData
      ?.financingType === "ESOP_FINANCING"
      ? "ESOP_FINANCING"
      : "SELF";
  const switchFinancing = (
    paymentType: PaymentType,
    financingType: "SELF" | "ESOP_FINANCING"
  ) => {
    const financier =
      paymentType === "Exercise Price"
        ? monetaryDetails.exerciseFinanciers?.[0]
        : monetaryDetails.taxFinanciers?.[0];
    const first = payments.find(
      (payment) => payment.paymentType === paymentType
    );
    setPayments((current) =>
      financingType === "ESOP_FINANCING"
        ? [
            ...current.filter((payment) => payment.paymentType !== paymentType),
            {
              ...first,
              paymentType,
              referenceId: financier?.name || "ESOP Financing",
              amount: targetFor(paymentType),
              currency: first?.currency || "INR",
              paymentDate: dateValue(),
              metaData: {
                financingType,
                financierId: financier?.id || "",
                financierName: financier?.name || "",
              },
            },
          ]
        : current.map((payment) =>
            payment.paymentType === paymentType
              ? {
                  ...payment,
                  metaData: { financingType: "SELF", modeOfPayment: "" },
                }
              : payment
          )
    );
  };
  const updateFinancier = (paymentType: PaymentType, financierId: string) => {
    const financiers =
      paymentType === "Exercise Price"
        ? monetaryDetails.exerciseFinanciers || []
        : monetaryDetails.taxFinanciers || [];
    const financier = financiers.find((item) => item.id === financierId);
    setPayments((current) =>
      current.map((payment) =>
        payment.paymentType === paymentType
          ? {
              ...payment,
              referenceId: financier?.name || "ESOP Financing",
              metaData: {
                ...payment.metaData,
                financingType: "ESOP_FINANCING",
                financierId,
                financierName: financier?.name || "",
              },
            }
          : payment
      )
    );
  };
  const validate = () => {
    if (!reason.trim()) return "Enter a correction reason before saving.";
    if (
      payments.some(
        (payment) =>
          !payment.referenceId.trim() ||
          !payment.paymentDate ||
          !Number.isFinite(payment.amount) ||
          payment.amount < 0
      )
    )
      return "Each tranche needs a reference ID, payment date, and valid amount.";
    if (
      payments.some(
        (payment) =>
          payment.metaData?.financingType === "ESOP_FINANCING" &&
          !payment.metaData?.financierId
      )
    )
      return "Select a financier for each ESOP-financed payment.";
    for (const type of types)
      if (Math.abs(totalFor(type) - targetFor(type)) > 0.01)
        return `${displayName[type]} must total ₹${targetFor(
          type
        ).toLocaleString("en-IN")}.`;
    return undefined;
  };
  const submit = async () => {
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(undefined);
    if (deletedCount && !confirming) {
      setConfirming(true);
      return;
    }
    if (!transactionId || !employee?.employeeId || !employee.requestId) return;
    setSaving(true);
    try {
      await correctApprovedPayments({
        transactionId,
        employeeId: employee.employeeId,
        requestId: employee.requestId,
        correctionReason: reason.trim(),
        paymentDetails: payments,
      });
      toast.success("Approved payment details corrected");
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Unable to correct payment details"
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <VStack className="w-full max-h-[calc(100vh-32px)] overflow-hidden bg-white">
      <HStack className="w-full items-start justify-between gap-4 border-b border-borderColor p-6">
        <VStack className="min-w-0 gap-1">
          <h2 className="text-base leading-6 font-semibold text-headerColor">
            Correct paid payment details
          </h2>
          <p className="text-sm leading-5 text-secondaryText">
            Changes remain approved and do not alter the exercise workflow.
          </p>
        </VStack>
        <button
          type="button"
          aria-label="Close correction dialog"
          title="Close"
          disabled={saving}
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed"
        >
          <Icon icon="material-symbols:close-rounded" width={20} height={20} />
        </button>
      </HStack>
      <VStack className="gap-6 overflow-y-auto px-6 py-5">
        {types.map((type) => {
          const total = totalFor(type);
          const target = targetFor(type);
          const financingEnabled =
            type === "Exercise Price"
              ? Boolean(monetaryDetails.exerciseEsopFinancingEnabled)
              : Boolean(monetaryDetails.taxEsopFinancingEnabled);
          const progress =
            target > 0 ? Math.min(100, (total / target) * 100) : 100;
          return (
            <Card key={type} className="border-borderColor shadow-none">
              <CardContent className="p-4">
                <VStack className="gap-4">
                  <HStack className="w-full items-start justify-between gap-4 pt-4">
                    <VStack className="gap-1">
                      <h3 className="text-base leading-6 font-semibold text-headerColor">
                        {displayName[type]}
                      </h3>
                      <p className="text-sm leading-5 text-secondaryText">
                        Approved:{" "}
                        <span className="font-semibold text-primaryText">
                          ₹{total.toLocaleString("en-IN")}
                        </span>{" "}
                        / ₹{target.toLocaleString("en-IN")}
                      </p>
                    </VStack>
                    <ButtonPrimary1
                      buttonSize={ButtonSize.MEDIUM}
                      disabled={financingFor(type) === "ESOP_FINANCING"}
                      onClick={() => add(type)}
                    >
                      Add tranche
                    </ButtonPrimary1>
                  </HStack>
                  {financingEnabled && (
                    <VStack className="gap-2">
                      <Label className="mb-0">Payment method</Label>
                      <RadioGroup
                        name={`${type}-financing-type`}
                        value={financingFor(type)}
                        onValueChange={(value) =>
                          switchFinancing(
                            type,
                            value as "SELF" | "ESOP_FINANCING"
                          )
                        }
                        disabled={saving}
                        className="grid-cols-1 gap-2 sm:grid-cols-2"
                      >
                        <Label className="mb-0 flex cursor-pointer items-center gap-2 rounded-lg border border-inputBorder bg-white p-3 text-sm text-primaryText">
                          <RadioGroupItem value="SELF" />
                          Self financed
                        </Label>
                        <Label className="mb-0 flex cursor-pointer items-center gap-2 rounded-lg border border-inputBorder bg-white p-3 text-sm text-primaryText">
                          <RadioGroupItem value="ESOP_FINANCING" />
                          ESOP Financing
                        </Label>
                      </RadioGroup>
                    </VStack>
                  )}
                  {financingFor(type) === "ESOP_FINANCING" && (
                    <VStack className="gap-2">
                      <Label className="required">Financier</Label>
                      <Select
                        options={
                          type === "Exercise Price"
                            ? monetaryDetails.exerciseFinanciers || []
                            : monetaryDetails.taxFinanciers || []
                        }
                        placeholder="Select financier"
                        valueGetter={(financier) => financier.id}
                        textGetter={(financier) => financier.name}
                        value={
                          payments.find(
                            (payment) => payment.paymentType === type
                          )?.metaData?.financierId || ""
                        }
                        onChange={(event) =>
                          updateFinancier(type, event.target.value)
                        }
                      />
                    </VStack>
                  )}
                  <div className="h-2 w-full overflow-hidden rounded-full bg-tertiaryBg">
                    <div
                      className="h-full rounded-full bg-green-501"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  {payments.map((payment, index) => {
                    if (payment.paymentType !== type) return null;
                    const source = payment.id
                      ? employee?.paymentDetails?.find(
                          (item) => item.id === payment.id
                        )
                      : undefined;
                    return (
                      <VStack
                        key={payment.id || `${type}-${index}`}
                        className="gap-4 border-t border-borderColor pt-4"
                      >
                        <HStack className="w-full justify-end">
                          <button
                            type="button"
                            aria-label="Remove tranche"
                            title="Remove tranche"
                            onClick={() =>
                              setPayments((current) =>
                                current.filter(
                                  (_, currentIndex) => currentIndex !== index
                                )
                              )
                            }
                            className="rounded p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <Icon icon="mdi:delete" width={20} height={20} />
                          </button>
                        </HStack>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          <VStack className="gap-2">
                            <Label>Reference ID</Label>
                            <Input
                              value={payment.referenceId}
                              onChange={(event) =>
                                update(index, "referenceId", event.target.value)
                              }
                            />
                          </VStack>
                          <VStack className="gap-2">
                            <Label>Payment date</Label>
                            <Input
                              type="date"
                              value={payment.paymentDate}
                              onChange={(event) =>
                                update(index, "paymentDate", event.target.value)
                              }
                            />
                          </VStack>
                          <VStack className="gap-2">
                            <Label>Amount</Label>
                            <Input
                              type="number"
                              min="0"
                              value={payment.amount}
                              onChange={(event) =>
                                update(index, "amount", event.target.value)
                              }
                            />
                          </VStack>
                          <VStack className="gap-2">
                            <Label>Currency</Label>
                            <Input
                              value={payment.currency}
                              onChange={(event) =>
                                update(index, "currency", event.target.value)
                              }
                            />
                          </VStack>
                          <VStack className="gap-2">
                            <Label>Payment mode</Label>
                            <Select
                              options={MODES}
                              placeholder="Select payment mode"
                              value={payment.metaData?.modeOfPayment || ""}
                              disabled={
                                payment.metaData?.financingType ===
                                "ESOP_FINANCING"
                              }
                              onChange={(event) =>
                                updateMode(index, event.target.value)
                              }
                            />
                          </VStack>
                        </div>
                        {source?.documentLinks?.length ? (
                          <VStack className="gap-1">
                            <span className="text-xs font-medium text-primaryText">
                              Attached receipts
                            </span>
                            <HStack className="flex-wrap gap-2">
                              {source.documentLinks.map(
                                (document, documentIndex) =>
                                  document.s3Url ? (
                                    <a
                                      key={document.id || documentIndex}
                                      className="text-xs2 text-primary hover:text-primaryHover hover:underline"
                                      href={document.s3Url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {document.name ||
                                        `Receipt ${documentIndex + 1}`}
                                    </a>
                                  ) : null
                              )}
                            </HStack>
                          </VStack>
                        ) : null}
                      </VStack>
                    );
                  })}
                </VStack>
              </CardContent>
            </Card>
          );
        })}
        <VStack className="gap-2">
          <Label className="required">Correction reason</Label>
          <TextArea
            value={reason}
            rows={4}
            required
            onChange={(event) => {
              setReason(event.target.value);
              setValidationError(undefined);
            }}
          />
          {validationError && (
            <p className="text-xs text-red-600" role="alert">
              {validationError}
            </p>
          )}
        </VStack>
        {confirming && (
          <p
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            role="alert"
          >
            {deletedCount} approved tranche{deletedCount === 1 ? "" : "s"} will
            be removed. Confirm to save this correction.
          </p>
        )}
      </VStack>
      <HStack className="w-full items-center justify-between gap-3 border-t border-borderColor px-6 py-4">
        <ButtonPrimary1
          buttonSize={ButtonSize.MEDIUM}
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </ButtonPrimary1>
        <ButtonPrimary
          buttonSize={ButtonSize.MEDIUM}
          loading={saving}
          loadingText="Saving correction..."
          onClick={submit}
        >
          {confirming ? "Confirm correction" : "Save correction"}
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
}
